import { sportHistoryFilters } from "./sportHistoryFilters";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import cn from "classnames";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import { formatTime, useTimeFormat } from "helpers/timeFormat";
import { useAppDispatch, useAppSelector } from "store";
import {
  getPlayerSportBetsList,
  handleBetsPaginate,
  SportBetsFilters,
} from "store/players/list/slice";
import { playersListLimits } from "configs/limits";

const SPORT_BETS_LIMIT = 10;

const initialFilters: SportBetsFilters = {
  createdFrom: "",
  createdTo: "",
  transactionId: "",
  itemUuid: "",
  bid: "",
  sport: "",
  action: "",
  betslipStatus: "",
  balanceType: "",
  eventName: "",
  amountMin: "",
  amountMax: "",
};

const displayValue = (value: unknown) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const getSportAction = (action: string | null, status: string | null) =>
  action === "refund" && status === "cash_out" ? "cash_out" : action;

const TruncatedText: React.FC<{ value: string | null | undefined }> = ({ value }) => (
  <div className="player-bets__truncated-text" title={displayValue(value)}>
    {displayValue(value)}
  </div>
);

const CopyableId: React.FC<{ value: string | null }> = ({ value }) => {
  if (!value) return <>—</>;

  return (
    <div className="player-bets__copyable-id" title={value}>
      <CopyToClipboard text={value} />
      <span>{value}</span>
    </div>
  );
};

const getBetbyBetIds = (bet: { outcomes?: Array<{ itemUuid?: string | null }> }) =>
  bet.outcomes
    ?.map((item) => item.itemUuid)
    .filter((value): value is string => Boolean(value))
    .join(", ") || null;

const ListSportTab: React.FC = () => {
  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<SportBetsFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<SportBetsFilters>
  >({});
  const [hasRequested, setHasRequested] = useState(false);
  const [currentLimit, setCurrentLimit] = useState(playersListLimits[0]);

  const bets = useAppSelector((store) => store.players.sportBetsList);
  const activePage = useAppSelector((store) => store.players.activePageBets);
  const totalItems = useAppSelector((store) => store.players.totalItemsBets);
  const totalPages = useAppSelector((store) => store.players.totalPagesBets);
  const isFetched = useAppSelector((store) => store.players.isBetsFetched);
  const error = useAppSelector((store) => store.players.betsError);

  const userId = searchParams.get("entityId");

  useEffect(() => {
    if (!userId) return;

    dispatch(
      getPlayerSportBetsList({
        id: Number(userId),
        limit: currentLimit,
        offset: activePage - 1,
        filters: appliedFilters,
      }),
    );
    setHasRequested(true);
  }, [activePage, appliedFilters, currentLimit, dispatch, userId]);

  const balanceTypeOptions = [
    {
      value: "real",
      label: t("real"),
    },
    {
      value: "bonus",
      label: t("bonus"),
    },
  ];

  const actionOptions = ["bet", "win", "refund", "cash_out", "rollback", "settle"].map((value) => ({
    value, label: t(`sportAction_${value}`),
  }));

  const displayStatus = (status: string | null) => {
    if (!status) return "—";
    const key = `sportStatus_${status}`;
    return t(key, { defaultValue: status });
  };

  const handleFilter = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setAppliedFilters({});
    dispatch(handleBetsPaginate(1));
  };

  const handleSearch = () => {
    const nextFilters = sportHistoryFilters(filters);
    setAppliedFilters(nextFilters);
    dispatch(handleBetsPaginate(1));
  };

  if (!hasRequested || !isFetched) {
    return (
      <div className="player-bets player-bets--sport">
        <ModalTabSkeleton />
      </div>
    );
  }

  return (
    <div className="player-bets player-bets--sport">
      <form
        className="player-bets__filters"
        onSubmit={(event) => {
          event.preventDefault();
          handleSearch();
        }}
      >
        <div className="player-bets__item">
          <InnerInput id="sport-betslip-status" type="text"
            label={t("sportBetStatus")} placeholder={t("sportProviderStatus")}
            value={filters.betslipStatus}
            onChange={(value) => handleFilter("betslipStatus", value)} />
        </div>
        <div className="player-bets__row player-bets__row--sport-primary">
          <div className="player-bets__item">
            <div className={cn("date-picker-wrapper")}>
              <div className="title">{t("startDate")}</div>

              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                dateFormat="dd.MM.yyyy"
                selected={
                  filters.createdFrom
                    ? moment(filters.createdFrom, "YYYY-MM-DD", true).toDate()
                    : null
                }
                onChange={(date) =>
                  handleFilter(
                    "createdFrom",
                    date ? moment(date).format("YYYY-MM-DD") : "",
                  )
                }
              />
            </div>
          </div>

          <div className="player-bets__item">
            <div className={cn("date-picker-wrapper")}>
              <div className="title">{t("endDate")}</div>

              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                dateFormat="dd.MM.yyyy"
                selected={
                  filters.createdTo
                    ? moment(filters.createdTo, "YYYY-MM-DD", true).toDate()
                    : null
                }
                onChange={(date) =>
                  handleFilter(
                    "createdTo",
                    date ? moment(date).format("YYYY-MM-DD") : "",
                  )
                }
              />
            </div>
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="sport-transaction-id"
              type="text"
              label={t("transactionId")}
              placeholder={t("enterId")}
              value={filters.transactionId}
              onChange={(value) => handleFilter("transactionId", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="betby-bet-id"
              type="text"
              label={t("sportSelectionId")}
              placeholder={t("enterId")}
              value={filters.itemUuid}
              onChange={(value) => handleFilter("itemUuid", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="bet-slip-id"
              type="text"
              label={t("sportVendorTransactionId")}
              placeholder={t("enterId")}
              value={filters.bid}
              onChange={(value) => handleFilter("bid", value)}
            />
          </div>
        </div>

        <div className="player-bets__row player-bets__row--sport-secondary">
          <div className="player-bets__item">
            <DefaultDropdown
              label={t("action")}
              placeholder={t("all")}
              value={filters.action}
              options={actionOptions}
              onSelect={(value) => handleFilter("action", String(value))}
            />
          </div>

          <div className="player-bets__item">
            <DefaultDropdown
              label={t("balanceType")}
              placeholder={t("all")}
              value={filters.balanceType}
              options={balanceTypeOptions}
              onSelect={(value) => handleFilter("balanceType", String(value))}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="sport-discipline"
              type="text"
              label={t("discipline")}
              placeholder={t("enterDiscipline")}
              value={filters.sport}
              onChange={(value) => handleFilter("sport", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="event-name"
              type="text"
              label={t("eventName")}
              placeholder={t("enterName")}
              value={filters.eventName}
              onChange={(value) => handleFilter("eventName", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="sport-amount-min"
              type="number"
              label={t("amountMin")}
              placeholder={t("min")}
              value={filters.amountMin}
              onChange={(value) => handleFilter("amountMin", value)}
            />
          </div>

          <div className="player-bets__item">
            <InnerInput
              id="sport-amount-max"
              type="number"
              label={t("amountMax")}
              placeholder={t("max")}
              value={filters.amountMax}
              onChange={(value) => handleFilter("amountMax", value)}
            />
          </div>

          <div className="player-bets__actions">
            <Button
              txt={t("reset")}
              isSecondary
              isSmall
              handleClick={handleReset}
            />
            <Button
              txt={t("search")}
              type="submit"
              isGreen
              isSmall
            />
          </div>
        </div>
      </form>

      <div className="inner-table-wrapper player-bets__table player-bets__table--sport">
        <table>
          <thead>
            <tr>
              <th>{t("id")}</th>
              <th>{t("date")}</th>
              <th>{t("action")}</th>
              <th>{t("sportBetStatus")}</th>
              <th>{t("amount")}</th>
              <th>{t("currency")}</th>
              <th>{t("sportBalance")}</th>
              <th>{t("discipline")}</th>
              <th>{t("event")}</th>
              <th>{t("market")}</th>
              <th>{t("outcome")}</th>
              <th>{t("odds")}</th>
              <th>{t("betType")}</th>
              <th>{t("sportBetbyId")}</th>
              <th>{t("sportSelectionId")}</th>
              <th>{t("sportVendorTransactionId")}</th>
            </tr>
          </thead>

          <tbody>
            {error && (
              <tr className="player-bets__empty-row player-bets__error-row">
                <td colSpan={16}>{t(error)}</td>
              </tr>
            )}

            {!error && bets.length === 0 && (
              <tr className="player-bets__empty-row">
                <td colSpan={16}>{t("noBetsFound")}</td>
              </tr>
            )}

            {!error &&
              bets.map((bet) => (
                <tr key={bet.id}>
                  <td>{displayValue(bet.id)}</td>
                  <td className="date" title={displayValue(bet.created)}>
                    {bet.created ? (
                      <>
                        <span>{moment(bet.created).format("DD.MM.YYYY")}</span>
                        <span>{formatTime(bet.created, timeFormat, true)}</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className={getSportAction(bet.action, bet.betslipStatus) ?? undefined}>
                    {t(`sportAction_${getSportAction(bet.action, bet.betslipStatus)}`, { defaultValue: bet.action || "—" })}
                  </td>
                  <td>{displayStatus(bet.betslipStatus)}</td>
                  <td>{displayValue(bet.amount)}</td>
                  <td>{displayValue(bet.currency)}</td>
                  <td title={displayValue(bet.balanceType)}>
                    {displayValue(bet.balanceType)}
                  </td>
                  <td>
                    {bet.outcomes?.length ? bet.outcomes.map((selection, index) => (
                      <div key={selection.itemUuid || index} title={displayValue(selection.event?.sport)}>
                        {displayValue(selection.event?.sport)}
                      </div>
                    )) : displayValue(bet.sport)}
                  </td>
                  <td>
                    {bet.outcomes?.length ? bet.outcomes.map((selection, index) => (
                      <TruncatedText key={selection.itemUuid || index} value={selection.event?.name} />
                    )) : <TruncatedText value={bet.eventName} />}
                  </td>
                  <td>
                    {bet.outcomes?.length ? bet.outcomes.map((selection, index) => (
                      <div key={selection.itemUuid || index} title={displayValue(selection.market)}>
                        {displayValue(selection.market)}
                      </div>
                    )) : displayValue(bet.market)}
                  </td>
                  <td>
                    {bet.outcomes?.length ? bet.outcomes.map((selection, index) => (
                      <TruncatedText key={selection.itemUuid || index} value={selection.outcome} />
                    )) : <TruncatedText value={bet.outcome} />}
                  </td>
                  <td>{displayValue(bet.odds)}</td>
                  <td title={displayValue(bet.betType)}>
                    {displayValue(bet.betType)}
                  </td>
                  <td>
                    <CopyableId value={bet.eventId} />
                  </td>
                  <td>
                    <CopyableId value={getBetbyBetIds(bet)} />
                  </td>
                  <td>
                    <CopyableId value={bet.bid} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!error && bets.length > 0 && (
        <Pagination
          activePage={activePage}
          limit={currentLimit}
          totalItems={totalItems}
          totalPages={totalPages}
          limitsArray={playersListLimits}
          handleLimitClick={(limit) => {
            setCurrentLimit(limit);
            dispatch(handleBetsPaginate(1));
          }}
          handlePrevClick={() => {
            if (activePage > 1) {
              dispatch(handleBetsPaginate(activePage - 1));
            }
          }}
          handleNextClick={() => {
            if (activePage < totalPages) {
              dispatch(handleBetsPaginate(activePage + 1));
            }
          }}
        />
      )}
    </div>
  );
};

export default ListSportTab;
